import { useState } from 'react';
import { useRef } from 'react';

import { Box, Card, Icon, Table, Text, VisuallyHidden } from '@zillow/constellation-10';
import { IconHomesFilled } from '@zillow/constellation-icons';

import { AccountViewListingGroupType } from '@zg-rentals/ts-rental-manager-api-client';

import { useTableObserver } from '../../../hooks/queries/properties/useTableObserver';
import { useGetPropertiesList } from '../../../hooks/queries/properties/zrm';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { getCurrentMonthString } from '../../../utils/dateUtils';
import { multiplexSkeletonPlaceholderListing, transformFiltersToApiParams } from '../../../utils/propertiesPageUtils';

import PropertiesConstants from '../../../constants/PropertiesConstants';
import PropertyConstants from '../../../constants/PropertyConstants';

import { useAuth } from '../../../components/AuthProvider';
import EmptyState from '../../../components/EmptyState';
import { FixedScrollbar } from '../../../components/properties/FixedScrollbar';
import { NoSearchResults } from '../../../components/properties/NoSearchResults.tsx';
import { stickyLeftCellRecipe } from '../../../components/properties/styles';
import { ZRMOverview } from '../../../components/properties/ZRMOverview';

import { cardBorderStyles } from '../../../styles/border';
import { TableHeaderCell } from '../styles';

import { emitPropertyCardHoverEvent } from '../../../analytics/events/propertiesPage';
import PropertyHeaderRow from '../PropertyHeaderRow';
import PropertyRow from '../PropertyRow';

import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';
import type { WebAnalyticsLeaseDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { FilterState } from '../../../utils/propertiesPageUtils';

export const PropertyTable = ({
  filters,
  setFilters,
  isSkeletonView = false,
}: {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  isSkeletonView?: boolean;
}) => {
  const { PROPERTY_TABLE_COLUMNS } = PropertyConstants;
  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const { data, isError, isFetching, isFetchingNextPage } = useGetPropertiesList(transformFiltersToApiParams(filters), {
    enabled: Boolean(!isSkeletonView),
  });

  const [hoveredCols, setHoveredCols] = useState<Record<string, boolean>>({});

  const tableRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { isScrolledToLeft, showFixedScrollbar, isTableBottomVisible } = useTableObserver({
    scrollContainerRef,
    isScreenMobile,
  });

  const { listingGroups = [], listingCount } = data ?? { listingCount: undefined };

  const isPageLoading = (isFetching && !isFetchingNextPage) || isSkeletonView;
  const listingsToRender = isPageLoading
    ? Array(PropertiesConstants.PROPERTIES_LIMIT_DEFAULT).fill(multiplexSkeletonPlaceholderListing())
    : listingGroups;

  const isArchivedView = filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED;
  const hasAnyArchivedListings = (listingCount?.archived || 0) > 0;

  const handleUpsellCellHover = (
    columnLabel: string,
    currentProperty: ZrmClickstreamListing,
    lease?: WebAnalyticsLeaseDto,
  ) => {
    if (!hoveredCols[columnLabel]) {
      emitPropertyCardHoverEvent({
        user,
        currentProperty,
        lease,
        triggerObject:
          PropertyConstants.PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS[
            columnLabel as keyof typeof PropertyConstants.PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS
          ],
      });
      setHoveredCols((prev) => ({ ...prev, [columnLabel]: true }));
    }
  };

  const handleRemoveSearchTextClick = () => {
    setFilters({ ...filters, searchText: '' });
  };

  const renderTimedOutDisplay = () => (
    <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginTop: '1200' }}>
      <Text
        css={{
          textAlign: 'center',
          padding: { base: '0 {spacing.400}', lg: '0' },
          marginBottom: '800',
          marginInline: '400',
        }}
      >
        We are having trouble retrieving your properties at this time. Please refresh the page or check back soon.
      </Text>
    </Box>
  );

  const renderNoListingsFound = () => {
    if (filters.searchText) {
      return <NoSearchResults showArchived={isArchivedView} onRemoveSearchTextClick={handleRemoveSearchTextClick} />;
    }

    if (isArchivedView) {
      return (
        <EmptyState
          icon={<Icon render={<IconHomesFilled />} size="md" css={{ color: 'icon.subtle' }} />}
          heading="No archived properties"
          headingLevel={3}
          align="center"
          useCard={false}
        />
      );
    }

    if (user?.features?.canAddListing) {
      return (
        <ZRMOverview
          hasAnyArchivedListings={hasAnyArchivedListings}
          handleShowArchivedClick={() => {
            setFilters({
              ...filters,
              subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
            });
          }}
        />
      );
    }

    return (
      <EmptyState
        icon={<Icon render={<IconHomesFilled />} size="md" css={{ color: 'icon.subtle' }} />}
        heading="No properties found"
        headingLevel={3}
        align="center"
        useCard={false}
      />
    );
  };

  const renderListingsDisplay = () => {
    if (listingsToRender.length > 0) {
      return (
        <Card tone="neutral" elevated={false} outlined={false} css={{ marginTop: '1200', ...cardBorderStyles }}>
          <Box
            data-testid="PropertiesPage-PropertyList"
            ref={tableRef}
            css={{
              overflow: { base: 'auto', lg: 'hidden' },
            }}
          >
            <Box
              ref={scrollContainerRef}
              css={{
                overflowX: { base: 'hidden', lg: 'auto' },
                maxWidth: '100%',
              }}
            >
              <Table.Root
                appearance="horizontal"
                aria-label="Properties list"
                size="md"
                data-testid={isPageLoading ? 'PropertiesPage-PropertyTableSkeleton' : 'PropertiesPage-PropertyTable'}
                css={{ tableLayout: 'fixed' }}
              >
                <Table.Header css={{ display: { base: 'none', lg: 'table-header-group' } }}>
                  <Table.Row>
                    <TableHeaderCell
                      id={PropertyConstants.PROPERTY_TABLE_COLUMNS.ADDRESS}
                      css={stickyLeftCellRecipe.raw({ scrolled: isScrolledToLeft })}
                    >
                      Address
                    </TableHeaderCell>
                    <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.RENT}>
                      {getCurrentMonthString(new Date())} rent
                    </TableHeaderCell>
                    <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS}>Potential tenants</TableHeaderCell>
                    <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.LEASES}>Lease</TableHeaderCell>
                    <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.ACTIONS}>
                      <VisuallyHidden>Actions</VisuallyHidden>
                    </TableHeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {listingsToRender.map((listing, index) =>
                    listing.type === AccountViewListingGroupType.MULTIPLEX ? (
                      <PropertyHeaderRow
                        key={listing.groupId}
                        listing={listing}
                        onUpsellCellHover={handleUpsellCellHover}
                        isScrolledToLeft={isScrolledToLeft}
                        filters={filters}
                      />
                    ) : (
                      <PropertyRow
                        key={isPageLoading ? index : listing.listingAlias}
                        index={index}
                        listing={listing}
                        filters={filters}
                        isLoading={isPageLoading}
                        onUpsellCellHover={handleUpsellCellHover}
                        isScrolledToLeft={isScrolledToLeft}
                      />
                    ),
                  )}
                </Table.Body>
              </Table.Root>
            </Box>

            <FixedScrollbar
              scrollContainerRef={scrollContainerRef}
              isVisible={showFixedScrollbar}
              isTableBottomVisible={isTableBottomVisible}
            />
          </Box>
        </Card>
      );
    }

    return (
      <Card
        outlined={false}
        elevated={false}
        css={{
          ...cardBorderStyles,
          marginBlock: '1200',
          backgroundColor: 'bg.neutral',
          textAlign: 'center',
          padding: '{spacing.1600} {spacing.800}',
          lg: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' },
        }}
      >
        {renderNoListingsFound()}
      </Card>
    );
  };

  return (
    <Box data-testid="PropertiesPage-PropertyTableContainer">
      {isError ? renderTimedOutDisplay() : renderListingsDisplay()}
    </Box>
  );
};
